<script>
import VuetifyTextClass from '@/constants/core/VuetifyTextClass'
import VuetifyColourClass from '@/constants/core/VuetifyColourClass'
import SimpleTableItem from '@/models/general/simpleTableItem'
import BaseButton from '@/components/general/_base-button.vue'
import baseMixin from '@/mixins/baseMixin'

export default {
  components: {
    BaseButton,
  },
  mixins: [baseMixin],
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      // Parents table items
      type: Array,
      default: () => [],
      validator: (items) =>
        items.every((item) => item instanceof SimpleTableItem),
    },
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
      validator: (items) =>
        items.every((item) => item instanceof SimpleTableItem),
    },
    removable: {
      type: Boolean,
      default: false,
    },
    disableRemove: {
      type: Boolean,
      default: false,
    },
    showIds: {
      type: Boolean,
      default: false,
    },
    excludedKeys: {
      type: Array,
      default: () => [],
    },
    allowedKeys: {
      type: Array,
      default: () => [],
    },
    height: {
      type: String,
      default: '',
    },
    centeredHeaders: {
      type: Array,
      default: () => [],
    },
    dense: {
      type: Boolean,
      default: false,
    },
    removeIconSize: {
      type: String,
      default: null,
    },
    removeTextSize: {
      type: String,
      default: '16px',
    },
    softRemovalKeys: {
      // hides strike through for the specified columns
      type: Array,
      default: () => [],
    },
    removeIcon: {
      type: String,
      default: 'mdi-delete',
    },
    removeHeader: {
      type: String,
      default: 'Remove',
    },
    rowHeight: {
      type: [String, Number],
      default: '',
    },
    headerFontSize: {
      type: String,
      default: null,
    },
    errorMessage: {
      type: [String, Boolean],
      default: '',
    },
  },
  data: () => ({
    ...VuetifyTextClass,
    text: VuetifyColourClass.text,
  }),
  computed: {
    activeItems() {
      return this.items.filter((item) => !item.removed)
    },
    useModeledItems() {
      return this.value?.length || this.removable
    },
    tableItems() {
      return this.useModeledItems ? this.value : this.items
    },
    allHeaders() {
      return [...this.headers, ...this.extraColumns]
    },
    allExcludedKeys() {
      const allExcludedKeys = [...this.excludedKeys]

      if (!this.showIds) allExcludedKeys.push('id')

      if (!this.removable) allExcludedKeys.push('removed')

      return allExcludedKeys
    },
    itemKeys() {
      if (this.allowedKeys.length) {
        return [...this.allowedKeys, ...this.extraColumns]
      }
      return [
        ...new Set(
          this.items.flatMap((item) =>
            Object.keys(item).filter(
              (key) => !this.allExcludedKeys.includes(key)
            )
          )
        ),
        ...this.extraColumns,
      ]
    },
    extraColumns() {
      const columns = []

      if (this.removable) columns.push('remove')

      return columns
    },
    rowStyle() {
      if (!this.rowHeight || isNaN(parseInt(this.rowHeight))) return {}

      return { height: `${parseInt(this.rowHeight)}px` }
    },
    headerStyles() {
      return this.headerFontSize
        ? { fontSize: this.headerFontSize + ' !important' }
        : {}
    },
  },

  methods: {
    toggleRemoved(id) {
      const updatedItem = this.value.find((item) => item.id === id)

      if (updatedItem) {
        updatedItem.removed = !updatedItem.removed
      }

      this.$emit('input', this.value)
    },
  },
}
</script>

<template>
  <v-simple-table
    v-bind="{ ...$attrs }"
    class="base-simple-table"
    :class="{ 'base-simple-table--dense': dense }"
    :style="{ maxHeight: height }"
  >
    <template v-slot>
      <thead class="simple-table-head">
        <tr>
          <th
            v-for="item in allHeaders"
            :key="item"
            class="text-left text-capitalize py-6"
            :style="headerStyles"
            :class="[
              caption,
              {
                'text-center':
                  item === 'remove' ||
                  centeredHeaders.map(({ title }) => title).includes(item),
              },
            ]"
            >{{ item === 'remove' ? removeHeader : item }}</th
          >
        </tr>
      </thead>
      <tbody>
        <tr class="table-alert-row">
          <td :colspan="allHeaders.length" class="pa-0 h-100 overflow-y-hidden">
            <transition name="slide-down">
              <v-alert
                v-if="!!errorMessage.length"
                type="error"
                elevation="1"
                tile
                dense
                class="text-body-1 mb-0"
              >
                {{ errorMessage || '' }}
              </v-alert>
            </transition>
          </td>
        </tr>

        <tr v-for="(item, index) in tableItems" :key="index" :style="rowStyle">
          <td v-for="key in itemKeys" :key="key" :class="smallBody">
            <template v-if="key === 'remove'">
              <div class="w-full d-flex justify-center align-center">
                <transition
                  :name="`slide-${item.removed ? 'right' : 'left'}-icon`"
                  mode="out-in"
                >
                  <BaseButton
                    v-if="item.removed"
                    key="undo"
                    text
                    :class="[smallBody]"
                    :icon-size="removeIconSize"
                    :color="isDarkMode ? '#bbbbbb' : 'grey500'"
                    :hover-color="isDarkMode ? 'grey300' : 'grey600'"
                    width="97"
                    :style="`font-size: ${removeIconSize};`"
                    @click="toggleRemoved(item.id)"
                  >
                    <span :style="`font-size: ${removeTextSize};`">{{
                      'undo'
                    }}</span>
                  </BaseButton>
                  <BaseButton
                    v-else
                    key="remove"
                    fab
                    :size="'xl'"
                    :disabled="disableRemove"
                    :icon="removeIcon"
                    :icon-size="removeIconSize"
                    text
                    :icon-hover-color="isDarkMode ? 'red lighten-1' : 'error'"
                    :hover-color="
                      isDarkMode ? 'red lighten-1' : 'error lighten-1'
                    "
                    :icon-color="isDarkMode ? 'red lighten-2' : 'grey'"
                    :color="isDarkMode ? 'red lighten-2' : 'grey'"
                    width="97"
                    @click="toggleRemoved(item.id)"
                  />
                </transition>
              </div>
            </template>
            <template v-else>
              <div
                class="cell"
                :class="{
                  removed: item.removed && !softRemovalKeys.includes(key),
                  'mx-auto': centeredHeaders
                    .map(({ value }) => value)
                    .includes(key),
                }"
              >
                <slot :name="`${key}-cell`" :item="item">
                  {{ item[key] }}
                </slot>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </template>
  </v-simple-table>
</template>

<style lang="scss" scoped>
@import '@/scss/_colors.scss';
@import '@/scss/_vue-transitions.scss';

.base-simple-table {
  border: 1px solid $color-grey-300;
  border-radius: 16px;
}

.base-simple-table ::v-deep .v-data-table__wrapper {
  height: auto !important;
  max-height: inherit !important;
  overflow-y: auto;
}

.base-simple-table thead th {
  background-color: #fafafa !important;

  &:first-child {
    border-radius: 16px 0 0 0;
  }
  &:last-child {
    border-radius: 0 16px 0 0;
  }
}

.base-simple-table ::v-deep td {
  min-height: 64px !important;
}

.base-simple-table--dense ::v-deep td {
  min-height: 48px !important;
}
.base-simple-table tr {
  border-bottom: none !important;
}

.base-simple-table td {
  border-bottom: none !important;
}

.simple-table-head {
  margin-bottom: 30px;
  color: $color-grey-600 !important;
  background-color: #fafafa;
}

.table-alert-row {
  height: 40px;

  &:hover {
    background: transparent !important;
  }
}

.cell {
  position: relative;
  width: fit-content;
}

.cell::after {
  position: absolute;
  bottom: 50%;
  left: 0;
  width: 0;
  height: 2px;
  content: '';
  background: $color-grey-600;
  opacity: 0.8;
  transition: width 0.2s ease;
}

.theme--dark .cell::after {
  background: $color-grey-200;
}

.removed.cell::after {
  width: 110%;
  margin: 0 -5%;
}

.theme--dark .base-simple-table thead th {
  color: #fafafa !important;
  background-color: #23272b !important;
}
</style>
